import React, { useState, useEffect, useRef } from "react";
import "./Automation.css";
import { useNavigate } from 'react-router-dom';
import AutomationHero from "./AutomationHero";
import ServiceWiseCode from "./ServiceWiseCode";
import HRCopilotNewsSection from "./HRCopilotNewsSection";
import HRCopilotFeatures from './HRCopilotFeatures';



const VideoPopup = ({ isOpen, onClose, videoUrl }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="automation-video-popup-overlay" onClick={onClose}>
      <div className="automation-video-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="automation-video-popup-close" onClick={onClose}>
          ×
        </button>
        <div className="automation-video-container">
          <video ref={videoRef} controls autoPlay className="automation-demo-video">
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="automation-video-popup-info">
          <h3>Service Automation Platform</h3>
          <p>See how our automation platform transforms service delivery and operational efficiency</p>
        </div>
      </div>
    </div>
  );
};

const Automation = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const navigate = useNavigate();
 const videoRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const handleAccessRecruit = () => navigate('/access-recruit');

  useEffect(() => {
    setIsVisible(true);
    
    // Auto rotate features
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 6);
    }, 4000);
    
    // Scroll progress
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const demoVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-automated-factory-assembly-line-42937-large.mp4";

  const openVideoPopup = () => {
    setIsVideoPopupOpen(true);
  };

  const closeVideoPopup = () => {
    setIsVideoPopupOpen(false);
  };

// Add these functions to your component
const handleAutomationDemo = () => navigate('/payment');

const openAutomationDemo = () => {
  // Open automation demo video/modal
  console.log('Opening automation demo');
  // Add your actual logic here
};

const handleAutomationIntegrations = () => {
  // Handle explore automation integrations click
  console.log('Exploring automation integrations');
  // Add your actual logic here
};

  const features = [
    {
      icon: "🤖",
      title: "AI-Powered Automation",
      description: "Intelligent workflows that learn and adapt to optimize service delivery processes"
    },
    {
      icon: "⚡",
      title: "Real-time Processing",
      description: "Instant service request handling with automated routing and prioritization"
    },
    {
      icon: "🔧",
      title: "Workflow Builder",
      description: "Drag-and-drop interface to create and customize automation workflows without coding"
    },
    {
      icon: "📊",
      title: "Analytics & Insights",
      description: "Comprehensive dashboards with performance metrics and optimization recommendations"
    },
    {
      icon: "🔄",
      title: "Seamless Integration",
      description: "Connect with existing systems and tools to create unified automation ecosystems"
    },
    {
      icon: "🛡️",
      title: "Enterprise Security",
      description: "Bank-grade security with compliance automation and audit trails"
    }
  ];

  const automationStages = [
    {
      phase: "Discovery & Analysis",
      title: "Process Identification & Mapping",
      steps: ["Process Mining", "Bottleneck Analysis", "Automation Potential", "ROI Calculation"],
      time: "Week 1-2"
    },
    {
      phase: "Implementation",
      title: "Workflow Development & Testing",
      steps: ["Workflow Design", "Bot Development", "Testing & QA", "Staging Deployment"],
      time: "Week 3-6"
    },
    {
      phase: "Optimization",
      title: "Continuous Improvement & Scaling",
      steps: ["Performance Monitoring", "Optimization", "Scale Planning", "Team Training"],
      time: "Ongoing"
    }
  ];

  return (
    <div className="automation-container">
      {/* Scroll Progress Bar */}
      {/* <div className="automation-scroll-progress" style={{width: `${scrollProgress}%`}}></div> */}

      {/* Video Popup */}
      <VideoPopup 
        isOpen={isVideoPopupOpen} 
        onClose={closeVideoPopup} 
        videoUrl={demoVideoUrl}
      />

      {/* Enhanced Animated Background */}
      <div className="automation-background-animation">
        {/* <div className="automation-floating-shape automation-shape-1"></div>
        <div className="automation-floating-shape automation-shape-2"></div>
        <div className="automation-floating-shape automation-shape-3"></div>
        <div className="automation-floating-shape automation-shape-4"></div>
        <div className="automation-floating-shape automation-shape-5"></div> */}
        
        {/* Animated Gradient Orbs */}
        {/* <div className="automation-gradient-orb automation-orb-1"></div>
        <div className="automation-gradient-orb automation-orb-2"></div>
        <div className="automation-gradient-orb automation-orb-3"></div> */}
        
        {/* SVG Background Animation */}
        <svg className="automation-bg-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="automation-gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(74, 144, 226, 0.08)" />
              <stop offset="50%" stopColor="rgba(120, 119, 216, 0.05)" />
              <stop offset="100%" stopColor="rgba(226, 74, 144, 0.08)" />
            </linearGradient>
            <linearGradient id="automation-gradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(120, 119, 216, 0.06)" />
              <stop offset="50%" stopColor="rgba(119, 216, 216, 0.04)" />
              <stop offset="100%" stopColor="rgba(74, 226, 144, 0.06)" />
            </linearGradient>
            <pattern id="automation-grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#automation-grid)" />
          <path 
            d="M0,15 Q30,5 60,25 T100,35 L100,100 L0,100 Z" 
            fill="url(#automation-gradient1)"
            className="automation-svg-path-1"
          />
          <path 
            d="M0,65 Q40,45 80,70 T100,85 L100,100 L0,100 Z" 
            fill="url(#automation-gradient2)"
            opacity="0.8"
            className="automation-svg-path-2"
          />
        </svg>
      </div>

<AutomationHero/>
<ServiceWiseCode/>
<HRCopilotFeatures/>
<HRCopilotNewsSection/>

    </div>
  );
};

export default Automation;